import { memoize } from "lodash";
import { observable, runInAction } from "mobx";
import {
  E_CLIENT_BREACH_LEVEL_STATE,
  TKhattabatConversations,
  TKhattabatMessage
} from "../../../Types";
import { isDefinedArray, isPositiveNumber, reduce } from "et3-type-guards";
import getKhattabat101CStore from ".";
import getKhattabatStore from "..";
import getCorePersonalStore from "../../core/core-personal-store";
import getKhattabatSharedMessagingStore from "../messaging-stores/conversation-messaging-store";
import { KHATTABAT_REACTIVATION_DELAY_DAYS } from "../../../constants/core-constants";
import { DEFAULT_TIME } from "../../../constants/input-entries-constants";
import {
  getDaysDifference,
  createDateFromSpecificDateString
} from "../../../helpers/et3-date-helper";
import getCorePopUpStore from "../../core/core-popup-store";
import { apiCallSetOneOOneMessagesSeen } from "../../../api/khattbat-api/api-call-set-one-o-one-messages-seen";
import getKhattabat101MessagingStore from "../messaging-stores/101-messaging-store";
import getKhattabat101CGeneralStore from "./101c-general-store";

class Khattabat101CMessagingStore {
  userID = observable.box<number>(0);

  updateClientConversation = ({
    clientConversation
  }: {
    clientConversation: TKhattabatConversations;
  }) => {
    const client101CStore = getKhattabat101CStore(this.userID.get());
    runInAction(() => {
      const alkhattabaUUID = getKhattabatStore().alkhattabaUUID.get();
      const alkhattabaUser = getCorePersonalStore().user.get();

      const sharedStore = getKhattabatSharedMessagingStore(
        client101CStore.oneOOneCId.get()
      );
      const messagingStore = getKhattabat101MessagingStore(
        client101CStore.oneOOneCId.get()
      );
      sharedStore.conversationType.set("101C");
      messagingStore.receiverID.set(client101CStore.userID.get());
      messagingStore.receiverUUID.set(client101CStore.userUUID.get());
      messagingStore.receiverName.set(client101CStore.userName.get());
      messagingStore.senderID.set(alkhattabaUser.id);
      messagingStore.senderUUID.set(alkhattabaUUID);
      messagingStore.senderName.set(alkhattabaUser.fullName);
      sharedStore.updateMessages(
        reduce(() => clientConversation.oneOOneCMessages, [])
      );

      const { conversationType, updateMessages } =
        getKhattabatSharedMessagingStore(
          `${client101CStore.oneOOneCId.get()}_COMMENTS`
        );
      const {
        receiverID,
        receiverUUID,
        receiverName,
        senderID,
        senderUUID,
        senderName
      } = getKhattabat101MessagingStore(
        `${client101CStore.oneOOneCId.get()}_COMMENTS`
      );
      conversationType.set("101C_COMMENTS");
      receiverID.set(client101CStore.userID.get());
      receiverUUID.set(client101CStore.userUUID.get());
      receiverName.set(client101CStore.userName.get());
      senderID.set(alkhattabaUser.id);
      senderUUID.set(alkhattabaUUID);
      senderName.set(alkhattabaUser.fullName);
      updateMessages(reduce(() => clientConversation.oneOOneCComments, []));
    });
  };
  clearClientConversation = () => {
    const oneOOneCId = getKhattabat101CStore(
      this.userID.get()
    ).oneOOneCId.get();
    runInAction(() => {
      getKhattabatSharedMessagingStore(oneOOneCId).resetMessages();
      getKhattabatSharedMessagingStore(
        `${oneOOneCId}_COMMENTS`
      ).resetMessages();
    });
  };

  getKhattabaClientToReactivateDetails = (): {
    hasBeenReactivated: boolean;
    stringifiedReactivatedAt: string;
    shouldShowReactivationProcess: boolean;
  } => {
    const client101CStore = getKhattabat101CStore(this.userID.get());
    const clientIsAlreadyHasBeenReactivated = isPositiveNumber(
      client101CStore.isOneOOneCHasBeenReactivated.get()
    );
    if (clientIsAlreadyHasBeenReactivated) {
      return {
        hasBeenReactivated: true,
        stringifiedReactivatedAt: `${client101CStore.oneOOneCReactivateDate.get()}`,
        shouldShowReactivationProcess: true
      };
    }

    return {
      hasBeenReactivated: false,
      stringifiedReactivatedAt: DEFAULT_TIME,
      shouldShowReactivationProcess: true
    };
  };
  requestReactivatingKhattabaClient = () => {
    //for more safety in case Khattaba opened affirmation Popup before this
    getCorePopUpStore().closeConfirmationPopUps();

    const client101CStore = getKhattabat101CStore(this.userID.get());
    const messages = getKhattabatSharedMessagingStore(
      client101CStore.oneOOneCId.get()
    ).messages.get();
    const isVIPKhatabat = client101CStore.isVIPKhatabat.get();

    if (!isDefinedArray(messages) || !isVIPKhatabat) {
      return getCorePopUpStore().showAlertMessage(
        "CANNOT_REACTIVATE_KHATTABA_CLIENT_POPUP"
      );
    }

    const clientMessages = messages.filter((message) => {
      return (
        isPositiveNumber(message.isClientMessage) &&
        !isPositiveNumber(message.isKhattabaMessage)
      );
    });
    //arr.at(-1) ==> get last element in the array
    const lastMessageDate = reduce(() => clientMessages.at(-1).createdOn, "");
    const dayDifferenceForLatestMessageSentByUser = getDaysDifference({
      dateFrom: createDateFromSpecificDateString(lastMessageDate),
      dateTo: createDateFromSpecificDateString("")
    });
    const canNotBeReactivatedYet =
      KHATTABAT_REACTIVATION_DELAY_DAYS >
      dayDifferenceForLatestMessageSentByUser;

    if (canNotBeReactivatedYet) {
      return getCorePopUpStore().showAlertMessage(
        "CANNOT_REACTIVATE_KHATTABA_CLIENT_POPUP"
      );
    }

    getKhattabat101CGeneralStore().setClientToBeReactivatedDetails({
      userID: this.userID.get()
    });
    return getCorePopUpStore().openConfirmationPopUps(
      "REACTIVATE_KHATTABA_CLIENT_POPUP"
    );
  };

  handleMessageSent = ({
    conversationID,
    oneOOneMessageToAdd
  }: {
    conversationID: string;
    oneOOneMessageToAdd: TKhattabatMessage;
  }) => {
    getKhattabatSharedMessagingStore(conversationID).appendMessage(
      oneOOneMessageToAdd
    );

    const isAiKhattaba = getCorePersonalStore().isKhattabaAiAdmin.get();
    if (!isAiKhattaba) {
      getKhattabat101CStore(this.userID.get()).updateBreachLevelStat({
        booleanKey:
          E_CLIENT_BREACH_LEVEL_STATE.HAS_NO_RC_MESSAGE_SINCE_MORE_THAN_54_HOURS,
        booleanKeyNewValue: false
      });
    }
  };

  markMessagesSeen = async () => {
    const userID = this.userID.get();
    const { status, code } = await apiCallSetOneOOneMessagesSeen({
      userID,
      conversationType: "101C"
    });
    if (status === "OK") {
      getKhattabatSharedMessagingStore(
        getKhattabat101CStore(userID).oneOOneCId.get()
      ).setUnreadMessagesCounter(0);
      getKhattabat101CStore(userID).updateBreachLevelStat({
        booleanKey:
          E_CLIENT_BREACH_LEVEL_STATE.HAS_UNREAD_MESSAGES_SINCE_MORE_THAN_18_HOURS,
        booleanKeyNewValue: false
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };
}

const getKhattabat101CMessagingStore = memoize((userID: number) => {
  const store = new Khattabat101CMessagingStore();
  store.userID.set(userID);
  return store;
});
export default getKhattabat101CMessagingStore;
